import { showNotificationError } from '@utils/functions'
import axios from 'axios'
import { Modal } from 'antd'
import { isArray, isUndefined } from 'lodash-es'
import { QueryClient } from 'react-query'

import localAccountManager from './localAccountManager'
import * as authService from './oauthService'

export let API_ACCESS_TOKEN = ''

export const GetAccessToken = () => {
  return API_ACCESS_TOKEN
}

if (import.meta.env.DEV) {
  API_ACCESS_TOKEN = sessionStorage.getItem('access_token') || ''
}

const headerConfig = {
  Authorization: 'Bearer ' + API_ACCESS_TOKEN,
  'X-Requested-With': 'XMLHttpRequest',
}

export const ApiRequest = axios.create({
  headers: headerConfig,
  baseURL: process.env.VITE_API_URL + '/api',
})

export const AppRequest = axios.create({
  headers: headerConfig,
  baseURL: process.env.VITE_API_URL + '/api/app',
})

export const RootRequest = axios.create({
  headers: headerConfig,
  baseURL: process.env.VITE_API_URL + '/',
})

// Define a function to refresh the access token
async function refreshAccessToken() {
  // Make a request to your backend to refresh the token
  // // await userManager.signinSilent()
  const resp = await RootRequest.post(
    '/connect/token',
    {
      refresh_token: localAccountManager.refreshToken, // Send the refresh token
      grant_type: 'refresh_token',
      client_id: 'ICS_App',
    },
    {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    }
  )

  return resp.data
}

// ─── Session timing configuration ─────────────────────────────────────────
// Defaults (seconds). Overridden at runtime by initSessionConfig() using
// the tenant settings AuthServer:RefreshTokenLifetimeMinutes and
// AuthServer:AccessTokenLifetimeMinutes.
// How long (seconds) the server keeps a session alive without activity
let AUTO_LOGOUT_SECONDS = 3600
// The token's own lifetime (seconds) as issued by the auth server
let TOKEN_EXPIRE_SECONDS = 1800
// How many seconds before the effective session end to show the warning
const WARN_BEFORE_SECONDS = 180
// Minimum gap (seconds) WARN_BEFORE_SECONDS must stay below TOKEN_EXPIRE_SECONDS.
// If violated, TOKEN_EXPIRE_SECONDS is bumped up so the warning still has room
// to fire before the access token expires.
const MIN_WARN_TOKEN_GAP_SECONDS = 60
// Floor for the warning lead-time when the remaining session is shorter than
// WARN_BEFORE_SECONDS. Ensures users still get a usable countdown window and
// prevents the warning from firing immediately on login for short-lived tokens.
const MIN_WARN_BEFORE_SECONDS = 60
// ──────────────────────────────────────────────────────────────────────────

/**
 * Apply session timing values from the current tenant settings object.
 * Call this after tenant data has been loaded (e.g. in appStore.init).
 *
 * Priority (per-tenant token lifetime settings win over legacy AuthServer keys):
 *   ICS.TokenLifetime.RefreshTokenMinutes       → AUTO_LOGOUT_SECONDS  (sliding idle window)
 *   ICS.TokenLifetime.AccessTokenMinutes         → TOKEN_EXPIRE_SECONDS (bearer token lifetime)
 *
 * Legacy fallback (used when ICS.TokenLifetime keys are absent):
 *   AuthServer:RefreshTokenLifetimeMinutes       → AUTO_LOGOUT_SECONDS
 *   AuthServer:AccessTokenLifetimeMinutes        → TOKEN_EXPIRE_SECONDS
 */
export function initSessionConfig(settings: Record<string, any> = {}) {
  const refreshMinutes = parseFloat(
    settings['ICS.TokenLifetime.RefreshTokenMinutes'] ??
      settings['AuthServer:RefreshTokenLifetimeMinutes']
  )
  const accessMinutes = parseFloat(
    settings['ICS.TokenLifetime.AccessTokenMinutes'] ??
      settings['AuthServer:AccessTokenLifetimeMinutes']
  )

  if (!isNaN(refreshMinutes) && refreshMinutes > 0) {
    AUTO_LOGOUT_SECONDS = refreshMinutes * 60
  }
  if (!isNaN(accessMinutes) && accessMinutes > 0) {
    TOKEN_EXPIRE_SECONDS = accessMinutes * 60
  }

  if (TOKEN_EXPIRE_SECONDS - WARN_BEFORE_SECONDS < MIN_WARN_TOKEN_GAP_SECONDS) {
    const adjusted = WARN_BEFORE_SECONDS + MIN_WARN_TOKEN_GAP_SECONDS
    console.warn(
      `[SessionConfig] TOKEN_EXPIRE_SECONDS (${TOKEN_EXPIRE_SECONDS}s) too close to WARN_BEFORE_SECONDS (${WARN_BEFORE_SECONDS}s); bumping to ${adjusted}s to preserve ${MIN_WARN_TOKEN_GAP_SECONDS}s gap.`
    )
    TOKEN_EXPIRE_SECONDS = adjusted
  }

  console.log(
    `[SessionConfig] AUTO_LOGOUT=${AUTO_LOGOUT_SECONDS}s, TOKEN_EXPIRE=${TOKEN_EXPIRE_SECONDS}s, GRACE=${
      AUTO_LOGOUT_SECONDS - TOKEN_EXPIRE_SECONDS
    }s`
  )
}

let sessionWatcherTimer: ReturnType<typeof setTimeout> | null = null

// ─── Cross-tab session sync ────────────────────────────────────────────────
const sessionChannel =
  typeof BroadcastChannel !== 'undefined'
    ? new BroadcastChannel('ics-session')
    : null

// Module-level refs so any tab event can close active modals
let activeExpiredModalRef: { destroy: () => void } | null = null
let activeWarningModalRef: { destroy: () => void } | null = null

function dismissSessionModals() {
  const warningRef = activeWarningModalRef
  activeWarningModalRef = null
  if (warningRef) warningRef.destroy()

  const expiredRef = activeExpiredModalRef
  activeExpiredModalRef = null
  if (expiredRef) expiredRef.destroy()
}
// ──────────────────────────────────────────────────────────────────────────────

const showSessionExpiredModal = () => {
  if (activeExpiredModalRef) return
  const ref = Modal.error({
    title: 'Session Expired',
    content:
      'Your session has expired due to exceeding inactivity or authorisation lifetime. Please sign in again to continue.',
    okText: 'Sign In',
    onOk: () => {
      activeExpiredModalRef = null
      authService.logout()
    },
  })
  activeExpiredModalRef = ref
}

const handleRefreshToken = async _config => {
  // Access token has expired; refresh it
  try {
    const data = await refreshAccessToken()
    // Update expireAt on the current account so the watcher uses the new expiry time
    if (data?.expires_in && localAccountManager.currentAccount) {
      localAccountManager.currentAccount.expireAt =
        Math.floor(Date.now() / 1000) + data.expires_in
    }
    localAccountManager.updateOidcUser(data)
    setAuthHeader(data?.access_token)
    // Notify other tabs so they can sync the new token immediately
    sessionChannel?.postMessage({
      type: 'TOKEN_REFRESHED',
      accessToken: data?.access_token,
      expiresIn: data?.expires_in,
    })
    // Restart the session expiry watcher with the new token's expiry
    startSessionExpiryWatcher()
    return data?.access_token
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 400) {
      clearSessionExpiryWatcher()
      showSessionExpiredModal()
    }
    throw error
  }
}

// ─── Session Expiry Watcher ────────────────────────────────────────────────

function clearSessionExpiryWatcher() {
  if (sessionWatcherTimer !== null) {
    clearTimeout(sessionWatcherTimer)
    sessionWatcherTimer = null
  }
}

function showSessionExpiryWarning(initialSeconds: number) {
  const expiresAtMs = Date.now() + Math.max(0, initialSeconds) * 1000
  let remaining = Math.max(0, Math.ceil((expiresAtMs - Date.now()) / 1000))
  let lastRenderedRemaining = remaining
  let countdownInterval: ReturnType<typeof setInterval> | null = null

  const getContent = (secs: number) =>
    `Your session will expire in ${secs} second${
      secs !== 1 ? 's' : ''
    }. Click Continue to stay signed in.`

  const stopCountdown = () => {
    if (countdownInterval) {
      clearInterval(countdownInterval)
      countdownInterval = null
    }
    document.removeEventListener('visibilitychange', syncCountdown)
  }

  const modal = Modal.confirm({
    title: 'Session Expiring Soon',
    content: getContent(remaining),
    okText: 'Continue',
    cancelText: 'Sign Out',
    onOk: async () => {
      stopCountdown()
      activeWarningModalRef = null
      await handleRefreshToken(null)
    },
    onCancel: () => {
      stopCountdown()
      activeWarningModalRef = null
      authService.logout()
    },
  })

  activeWarningModalRef = {
    destroy: () => {
      stopCountdown()
      activeWarningModalRef = null
      modal.destroy()
    },
  }

  const syncCountdown = () => {
    remaining = Math.max(0, Math.ceil((expiresAtMs - Date.now()) / 1000))

    if (remaining <= 0) {
      stopCountdown()
      activeWarningModalRef = null
      modal.destroy()
      showSessionExpiredModal()
      return
    }

    if (remaining !== lastRenderedRemaining) {
      lastRenderedRemaining = remaining
      modal.update({ content: getContent(remaining) })
    }
  }

  countdownInterval = setInterval(syncCountdown, 1000)

  // Timers are throttled in background tabs; force re-sync when tab becomes visible.
  document.addEventListener('visibilitychange', syncCountdown)
}

export function startSessionExpiryWatcher() {
  clearSessionExpiryWatcher()

  const expireAt = localAccountManager.currentAccount?.expireAt
  if (!expireAt) return

  const now = Math.floor(Date.now() / 1000)

  // The server keeps the session alive for AUTO_LOGOUT_SECONDS total, but
  // the token itself only lives TOKEN_EXPIRE_SECONDS. The difference is the
  // grace period after token expiry before the server truly logs the user out.
  const SESSION_GRACE_SECONDS = AUTO_LOGOUT_SECONDS - TOKEN_EXPIRE_SECONDS
  const effectiveExpiry = expireAt + SESSION_GRACE_SECONDS
  const secondsUntilEffectiveExpiry = effectiveExpiry - now

  if (secondsUntilEffectiveExpiry <= 0) return

  // Clamp the warning lead-time so it stays strictly less than the remaining
  // session (otherwise secondsUntilWarn would be <= 0 and the modal would fire
  // immediately on login — observed when TOKEN_EXPIRE_SECONDS is very small).
  // Floor at MIN_WARN_BEFORE_SECONDS so users still get a usable countdown.
  const effectiveWarnBefore = Math.min(
    WARN_BEFORE_SECONDS,
    Math.max(
      MIN_WARN_BEFORE_SECONDS,
      secondsUntilEffectiveExpiry - MIN_WARN_BEFORE_SECONDS
    )
  )

  const secondsUntilWarn = secondsUntilEffectiveExpiry - effectiveWarnBefore

  if (secondsUntilWarn <= 0) {
    // Already within the warning window — show warning immediately
    console.log(
      `[SessionWatcher] Already within warning window. Showing warning now (${secondsUntilEffectiveExpiry}s until session ends).`
    )
    showSessionExpiryWarning(secondsUntilEffectiveExpiry)
    return
  }

  console.log(
    `[SessionWatcher] Session warning modal will appear in ${secondsUntilWarn}s (effective expiry in ${secondsUntilEffectiveExpiry}s, warn-before=${effectiveWarnBefore}s).`
  )
  sessionWatcherTimer = setTimeout(() => {
    showSessionExpiryWarning(effectiveWarnBefore)
  }, secondsUntilWarn * 1000)
}

// ──────────────────────────────────────────────────────────────────────────────

// Add a request interceptor
ApiRequest.interceptors.response.use(
  response => {
    return response
  },
  async error => {
    const originalRequest = error.config
    // Check if the error status code is 401 (unauthorized) and handle token refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true

      try {
        // Refresh the token
        const accessToken = await handleRefreshToken(ApiRequest)
        originalRequest.headers.Authorization = 'Bearer ' + accessToken
        // Retry the original request with the new token
        return ApiRequest(originalRequest)
      } catch (refreshError) {
        // Handle refresh token failure (e.g., logout the user)
        return Promise.reject(refreshError)
      }
    }

    return Promise.reject(error)
  }
)

const successResponseInterceptor = resp => {
  return resp
}

const errorResponseInterceptor = data => {
  const status = data.response?.status
  const config = data.response?.config
  if (
    (status === 401 || status == 500) &&
    config.url === 'app/account/my-profile'
  ) {
    return Promise.reject(data)
  }

  if (status === 408) {
    const errorMessage =
      'This function is taking longer than expected to complete. Please try again in a few minutes.'
    showNotificationError(errorMessage)
    return Promise.reject(errorMessage)
  }

  if (status === 409) {
    const errorMessage =
      "The data you're working on has been modified by other user. Please refresh to get latest data and try again."
    showNotificationError(errorMessage)
    return Promise.reject(errorMessage)
  }

  if (status !== 500) {
    showNotificationError(data)
  }

  return Promise.reject(data)
}

ApiRequest.interceptors.response.use(
  successResponseInterceptor,
  errorResponseInterceptor
)
AppRequest.interceptors.response.use(
  successResponseInterceptor,
  errorResponseInterceptor
)
RootRequest.interceptors.response.use(
  successResponseInterceptor,
  errorResponseInterceptor
)

export const setAuthHeader = token => {
  ApiRequest.defaults.headers.Authorization = 'Bearer ' + token
  AppRequest.defaults.headers.Authorization = 'Bearer ' + token
  RootRequest.defaults.headers.Authorization = 'Bearer ' + token

  API_ACCESS_TOKEN = token

  if (import.meta.env.DEV) {
    sessionStorage.setItem('access_token', token)
  } else {
  }
}

export const clearAuthHeader = () => {
  ApiRequest.defaults.headers.Authorization = undefined
  AppRequest.defaults.headers.Authorization = undefined
  RootRequest.defaults.headers.Authorization = undefined
}

// ─── Cross-tab token refresh listener ────────────────────────────────────────
// When another tab refreshes the token, sync auth headers, dismiss stale modals,
// and restart the session watcher in this tab.
if (sessionChannel) {
  sessionChannel.onmessage = event => {
    const msg = event.data
    if (msg?.type === 'TOKEN_REFRESHED') {
      if (msg.expiresIn && localAccountManager.currentAccount) {
        localAccountManager.currentAccount.expireAt =
          Math.floor(Date.now() / 1000) + msg.expiresIn
      }
      setAuthHeader(msg.accessToken)
      dismissSessionModals()
      startSessionExpiryWatcher()
    }
  }
}
// ──────────────────────────────────────────────────────────────────────────────

export const queryClient = new QueryClient()

export interface QueryOptions {
  disableCache?: boolean
  /**
   * Cache time in seconds
   */
  staleTime?: number
}
export function CreateParams(params) {
  let qs = []

  Object.keys(params).forEach(key => {
    const value = params[key]

    if (isArray(value)) {
      value.forEach(i => {
        qs.push(`${key}=${i}`)
      })
    } else {
      if (value || typeof value === 'boolean') {
        qs.push(`${key}=${isUndefined(value) ? '' : value}`)
      }
    }
  })
  return qs
}

export function GetBaseServiceFunctions(baseRoute) {
  async function GetAll(params: any = {}, options?: QueryOptions) {
    if (params && !params.maxResultCount) {
      params.maxResultCount = window.APP_CONFIG.maxResultCount
    }

    if (options?.disableCache) {
      await queryClient.invalidateQueries([baseRoute])
    }
    return queryClient.fetchQuery(
      [baseRoute, params],
      () => {
        return ApiRequest.get(baseRoute, {
          params: params,
          paramsSerializer: {
            indexes: true,
          },
        })
      },
      {
        staleTime: (options?.staleTime || 60) * 1000,
      }
    )
  }

  async function GetById(id, options?: QueryOptions) {
    if (options?.disableCache) {
      await queryClient.invalidateQueries([baseRoute])
    }
    return queryClient.fetchQuery(
      [baseRoute, id],
      () => {
        return ApiRequest.get(`${baseRoute}/${id}`)
      },
      {
        staleTime: 60000,
      }
    )
  }

  function GetMyDetail() {
    return ApiRequest.get(`${baseRoute}/myDetail`)
  }

  async function Create(data) {
    const resp = await ApiRequest.post(baseRoute, data)
    await queryClient.invalidateQueries([baseRoute])
    return resp
  }

  async function Update(id, data) {
    await queryClient.invalidateQueries([baseRoute, id])
    await queryClient.invalidateQueries([baseRoute])
    const resp = await ApiRequest.put(`${baseRoute}/${id}`, data)
    return resp
  }

  // async function UpdateEmailUsername(id, data) {
  //   await queryClient.invalidateQueries([baseRoute, id])
  //   await queryClient.invalidateQueries([baseRoute])
  //   const resp = await ApiRequest.put(
  //     `${baseRoute}/UpdateEmailUsername/${id}`,
  //     data
  //   )
  //   return resp
  // }

  async function Delete(id) {
    const resp = await ApiRequest.delete(`${baseRoute}/${id}`)
    await queryClient.invalidateQueries([baseRoute])
    return resp
  }

  return {
    GetAll: GetAll,
    GetById: GetById,
    GetMyDetail: GetMyDetail,
    Create: Create,
    Update: Update,
    Delete: Delete,
  }
}
